import RefreshIcon from "@assets/images/refresh-primary.svg";
import {
  Box,
  Button,
  Collapse,
  Divider,
  FormControlLabel,
  Grid,
  GridSize,
  Radio,
  RadioGroup
} from "@material-ui/core";
import SearchIcon from "@material-ui/icons/Search";
import InputSuggestion from "@saleor/alternatedMaterial/components/InputSuggestionForPrioridy";
import OrderStatus from "@saleor/changeOrderExport/components/OrderStatus";
import DatePicker from "@saleor/components/DatePicker";
import { FormSpacer } from "@saleor/components/FormSpacer";
import MyTypography from "@saleor/components/MyTypography";
import SingleSelectField from "@saleor/components/SingleSelectField";
import { SingleTextField } from "@saleor/components/SingleTextField";
import useAppState from "@saleor/hooks/useAppState";
import useOrderFilterDomestic from "@saleor/hooks/useOrderFilterDomestic";
import { mapEdgesToItems } from "@saleor/utils/maps";
import moment from "moment-timezone";
import React, { useEffect, useMemo } from "react";
import SVG from "react-inlinesvg";
import { FormattedMessage } from "react-intl";

import { OrderListFilterProps } from "../OrderListFilter";
import { DateSelectionType, FIELD_TYPE } from "./constant";
import useStyles from "./styles";

const OrderFilterSection: React.FC<OrderListFilterProps> = ({
  handleChangeFilter,
  message,
  listOrder,
  loading,
  setIsSearched
}) => {
  const classes = useStyles();
  const [{ filterOrder: filterAppState }] = useAppState();
  const {
    formik,
    setDataFormik,
    isOpen,
    setIsOpen,
    fetchSoldToDataSuggestions,
    loadingSoldToDataSuggestion,
    getSoldToDataSuggestions,
    formData,
    ArrowIconComponent,
    orderStatus,
    getRequireAttentionMaterial,
    fetchRequireAttentionMaterial,
    loadingRequireAttentionMaterial,
    getSaleEmployeeDataSuggestions,
    fetchSaleEmployeeDataSuggestions,
    loadingSaleEmployeeDataSuggestion,
    handleCleanForm,
    isClean
  } = useOrderFilterDomestic({ handleChangeFilter });

  useEffect(() => {
    if (!filterAppState) {
      handleCleanForm();
      return;
    }
    setDataFormik({
      ...filterAppState,
      gte: filterAppState.createDate
        ? filterAppState.createDate.gte
        : filterAppState.lastUpdate
        ? filterAppState.lastUpdate.gte
        : "",
      lte: filterAppState.createDate
        ? filterAppState.createDate.lte
        : filterAppState.lastUpdate
        ? filterAppState.lastUpdate.lte
        : "",
      dateSelectionType: filterAppState.createDate
        ? DateSelectionType.CREATE_DATE
        : DateSelectionType.LAST_UPDATE,
      statusSAP: filterAppState.statusSap?.length
        ? filterAppState.statusSap?.length === 1
          ? filterAppState.statusSap[0]
          : "ALL"
        : ""
    });
  }, [filterAppState]);

  const {
    bu,
    company,
    soldTo,
    poNo,
    soNo,
    contractNo,
    dpNo,
    invoiceNo,
    materialCodeName,
    saleGroup,
    saleEmployee,
    orderType
  } = formik.values;

  useEffect(() => {
    if (
      !!(
        (bu || company || soldTo) &&
        !soNo &&
        !poNo &&
        !contractNo &&
        !dpNo &&
        !invoiceNo &&
        !materialCodeName &&
        !saleGroup &&
        !saleEmployee &&
        !orderType
      )
    ) {
      formik.setFieldValue("dateSelectionType", DateSelectionType.CREATE_DATE);
    }
  }, [formik.values]);

  useEffect(() => {
    if (listOrder?.length) {
      setIsOpen(false);
    }
  }, [listOrder]);

  const createdDateRange = useMemo(() => {
    const { soldTo = "", materialCodeName = "" } = formik.values ?? {};
    const soldToTextCount = soldTo.length;
    const materialCodeTextCount = materialCodeName.length;

    if (
      soldToTextCount > 0 ||
      (soldToTextCount === 0 && materialCodeTextCount > 0)
    ) {
      return 91;
    }
    return 31;
  }, [formik.values.soldTo, formik.values.materialCodeName]);

  useEffect(() => {
    const { gte: createDateStart, lte: createDateEnd } = formik.values;
    const maxDiff = createdDateRange - 1;

    if (createDateStart && createDateEnd) {
      const diffDays = moment(createDateStart).diff(
        moment(createDateEnd),
        "days"
      );
      if (Math.abs(diffDays) > maxDiff) {
        formik.setFieldValue("gte", "");
        formik.setFieldValue("lte", "");
      }
    }
  }, [createdDateRange]);

  useEffect(() => {
    if (formik.values.lte) {
      const maxDiff = createdDateRange - 1;

      const diff = moment(formik.values.lte).diff(
        moment(formik.values.gte),
        "day"
      );
      if (Math.abs(diff) > maxDiff) {
        const newLte = moment(formik.values.gte).add(maxDiff, "day");
        formik.setFieldValue("lte", newLte);
      }
    }
  }, [formik.values.gte]);

  useEffect(() => {
    if (formik.values.gte) {
      const maxDiff = createdDateRange - 1;

      const diff = moment(formik.values.gte).diff(
        moment(formik.values.lte),
        "day"
      );
      if (Math.abs(diff) > maxDiff) {
        const newGte = moment(formik.values.lte).subtract(maxDiff, "day");
        formik.setFieldValue("gte", newGte);
      }
    }
  }, [formik.values.lte]);

  const getTextError = field => (
    <div className={classes.textError}>{formik.errors[field]}</div>
  );

  const isDisabled = useMemo(() => {
    if (!formik.values.company && !formik.values.soNo && !formik.values.poNo) {
      return true;
    }

    if (
      (formik.values.gte && !formik.values.lte) ||
      (!formik.values.gte && formik.values.lte)
    ) {
      return true;
    }

    if (
      !formik.values.soNo &&
      !formik.values.poNo &&
      !formik.values.invoiceNo &&
      !formik.values.contractNo &&
      !formik.values.dpNo &&
      !formik.values.lte &&
      !formik.values.gte
    ) {
      return true;
    }
    return !formik.dirty;
  }, [formik.values, formik.dirty]);

  const mappingDataSuggest = name => {
    if (name === "soldTo") {
      const filterSoldToDomesticOrder =
        fetchSoldToDataSuggestions?.filterSoldToDomesticOrder;
      return {
        totalCount: filterSoldToDomesticOrder?.edges?.length,
        pageInfo: filterSoldToDomesticOrder?.pageInfo,
        showLoadMore: true,
        fetchDataSuggestions: fetchSoldToDataSuggestions,
        loadingDataSuggestion: loadingSoldToDataSuggestion,
        getDataSuggestions: getSoldToDataSuggestions,
        searchField: "search",
        dataOptions:
          mapEdgesToItems(filterSoldToDomesticOrder)?.map(data => ({
            title: `${data.code} - ${data.name}`,
            value: data.code
          })) || []
      };
    }
    if (name === "materialCodeName") {
      return {
        fetchDataSuggestions: fetchRequireAttentionMaterial,
        loadingDataSuggestion: loadingRequireAttentionMaterial,
        getDataSuggestions: getRequireAttentionMaterial,
        searchField: "material",
        isCheckingRole: true,
        dataOptions:
          mapEdgesToItems(
            fetchRequireAttentionMaterial?.filterRequireAttentionMaterial
          )?.map(data => ({
            title: `${data.code} - ${data.descriptionEn}`,
            value: data.code
          })) || [],
        totalCount:
          fetchRequireAttentionMaterial?.filterRequireAttentionMaterial?.edges
            ?.length
      };
    }
    if (name === "saleEmployee") {
      return {
        fetchDataSuggestions: fetchSaleEmployeeDataSuggestions,
        loadingDataSuggestion: loadingSaleEmployeeDataSuggestion,
        getDataSuggestions: getSaleEmployeeDataSuggestions,
        searchField: "salesEmployeee",
        dataOptions:
          mapEdgesToItems(
            fetchSaleEmployeeDataSuggestions?.filterSaleEmployeeDomesticOrder
          )?.map(data => ({
            title: `${data.code} - ${data.name}`,
            value: `${data.code} - ${data.name}`
          })) || []
      };
    }
  };

  const renderItem = (
    <Grid className={classes.searchBox} container spacing={3}>
      {formData.map((child, key) => {
        const renderChild = (children: JSX.Element) => (
          <Grid
            item
            md={child.md ? (child.md as GridSize) : 2}
            sm={child.sm ? (child.sm as GridSize) : 6}
            key={child.name}
          >
            <MyTypography variant={"subtitle3"}>
              {child.title}
              {child.required ? (
                <span className={classes.required}>*</span>
              ) : null}
            </MyTypography>
            {children}
            {getTextError(child.name)}
          </Grid>
        );
        if (child.type === FIELD_TYPE.SINGLE_INPUT) {
          return renderChild(
            <SingleTextField
              name={child.name}
              id={child.name}
              InputProps={{
                inputProps: { "data-testid": `${child.name}-input` }
              }}
              value={formik.values[child.name]}
              onChange={formik.handleChange}
              placeholder={child.placeholder}
              error={!!formik.errors[child.name]}
              maxLengthProps={child?.maxLength ? child.maxLength : undefined}
            />
          );
        }
        if (child.type === FIELD_TYPE.SINGLE_SELECT) {
          return renderChild(
            <SingleSelectField
              className={classes.selectField}
              menuListCustomStyle={{
                overflowY: "auto"
              }}
              choices={child.options}
              name={child.name}
              id={child.name}
              testId={`${child.name}-select`}
              InputProps={{
                inputProps: { "data-testid": `${child.name}-input` },
                placeholder: child.placeholder
              }}
              onChange={formik.handleChange}
              value={formik.values[child.name]}
              placeholder={child.placeholder}
              error={!!formik.errors[child.name]}
              disabled={child.disabled}
            />
          );
        }

        if (child.type === FIELD_TYPE.SUGGESTION_INPUT) {
          const dataSuggest = mappingDataSuggest(child.name);

          return renderChild(
            <InputSuggestion
              {...dataSuggest}
              contained
              key={`input${key}`}
              name={child.name}
              id={child.name}
              testId={child.name}
              withNonSelect
              defaultInputValue={formik.values[child.name]}
              defaultValue={{
                title: formik.values[child.name],
                value: formik.values[child.name]
              }}
              objectSuggestion
              placeholder={child.placeholder}
              error={!!formik.errors[child.name]}
              isClean={isClean}
              handleSelectedSearch={e => {
                formik.handleChange({
                  target: {
                    name: child.name,
                    value: typeof e === "string" ? e : e.value
                  }
                });
              }}
            />
          );
        }

        if (child.type === FIELD_TYPE.RADIO_GROUP) {
          return renderChild(
            <RadioGroup
              className={classes.radioGroup}
              aria-labelledby="demo-controlled-radio-buttons-group"
              name={child.name}
              id={child.name}
              data-testid={child.name}
              value={formik.values[child.name]}
              data-test-value={formik.values[child.name]}
              onChange={formik.handleChange}
            >
              {child.options.map((o, oKey) => (
                <FormControlLabel
                  key={`radio${oKey}`}
                  id={`radio${oKey}`}
                  data-testid={`${child.name}-option`}
                  value={o.value}
                  disabled={
                    !!(
                      (bu || company || soldTo) &&
                      !soNo &&
                      !contractNo &&
                      !dpNo &&
                      !invoiceNo &&
                      !materialCodeName &&
                      !saleGroup &&
                      !saleEmployee &&
                      !orderType
                    )
                  }
                  control={<Radio />}
                  label={
                    <MyTypography variant={"body3"}>{o.label}</MyTypography>
                  }
                />
              ))}
            </RadioGroup>
          );
        }
        if (child.type === FIELD_TYPE.DATEPICKER) {
          let maxDate = moment();
          let minDate;
          // this is start date
          if (child.name === "gte" && formik.values.lte) {
            minDate = undefined;
            maxDate = moment(formik.values.lte).isAfter(moment())
              ? moment()
              : moment(formik.values.lte);
          }
          // this is end date
          if (child.name === "lte" && formik.values.gte) {
            // const addMonth = moment(formik.values.gte).add(3, "month");
            maxDate = undefined;
            minDate = moment(formik.values.gte);
          }
          return renderChild(
            <DatePicker
              placement={"top"}
              name={child.name}
              id={child.name}
              disableFuture
              disableManualInput
              isAllowTodayDate
              value={formik.values[child.name] || ""}
              onChange={formik.handleChange}
              format="YYYY-MM-DD"
              maxDate={maxDate ? maxDate.toDate() : undefined}
              minDate={minDate ? minDate.toDate() : undefined}
              noBlock={true}
              dateRange={createdDateRange}
              error={!!formik.errors[child.name]}
            />
          );
        }
        if (child.type === FIELD_TYPE.MULTIPLE_SELECT) {
          return renderChild(
            <OrderStatus
              showChipDeleteButton={false}
              choices={orderStatus}
              name={child.name}
              id={child.name}
              value={formik.values[child.name]}
              onChange={formik.handleChange}
              isClean={isClean}
              placeholder={child.placeholder}
              numberItemDisplay={2}
              error={!!formik.errors[child.name]}
            />
          );
        }
      })}
    </Grid>
  );

  const onClickCleanForm = () => {
    setIsSearched(false);
    handleCleanForm();
  };

  return (
    <>
      <Grid
        className={classes.header}
        container
        data-testid="orderFilterSection-collapseButton"
        onClick={() => setIsOpen(!isOpen)}
      >
        <MyTypography variant="h6">
          <FormattedMessage defaultMessage="ค้นหาคำสั่งซื้อ" />
        </MyTypography>
        <ArrowIconComponent className={classes.arrowIcon} />
      </Grid>
      <Collapse
        data-testid="orderFilterSection-collapseForm"
        data-test-in={isOpen}
        in={isOpen}
      >
        {renderItem}
        <Box className={classes.divider}>
          <FormSpacer />
          <Divider />
        </Box>
        <Box className={classes.bottomBar} ml={3}>
          {message}
          <Box className={classes.flex}>
            <Box mr={2}>
              <Button
                className={classes.resetButton}
                variant={"outlined"}
                color={"secondary"}
                disabled={!formik.dirty || loading}
                onClick={onClickCleanForm}
                name="resetButton"
                id="resetButton"
                data-testid="resetButton"
              >
                <SVG
                  src={RefreshIcon}
                  className={classes.icon}
                  width={16}
                  height={16}
                />
                <FormattedMessage defaultMessage="ล้างข้อมูล" />
              </Button>
            </Box>
            <Box>
              <Button
                className={classes.actionButton}
                variant={"contained"}
                disabled={isDisabled || loading}
                color={"secondary"}
                onClick={formik.submitForm}
                name="submitButton"
                id="submitButton"
                data-testid="submitButton"
              >
                <SearchIcon style={{ fontSize: "18px" }} />
                <FormattedMessage defaultMessage="ค้นหา" />
              </Button>
            </Box>
          </Box>
        </Box>
      </Collapse>
    </>
  );
};

export default OrderFilterSection;
